import { registerOverlay } from 'klinecharts'
import { isNum, resolvePricePrecision } from './overlayUtils'
import {
  DEFAULT_FIGURES,
  NO_DEFAULT_FIGURES,
  type OverlayTemplateParam,
  registerOnce,
} from './registrationUtils'

/**
 * Fibonacci retracement with per-level colors.
 * Overrides the built-in 'fibonacciLine' overlay.
 *
 * Levels are per-instance overridable — colour and visibility — via the
 * overlay's `extendData`. See
 * docs/superpowers/specs/2026-07-29-fibonacci-level-visibility-design.md
 */

/** One built-in level. `key` is the stable identity used by overrides and
 *  figure keys; never key off `percent`, whose float formatting is an
 *  accident waiting to happen. */
export interface FibLevel {
  key: string
  percent: number
  color: string
}

export const FIB_LEVELS: readonly FibLevel[] = [
  { key: '0', percent: 0, color: '#787B86' }, // gray
  { key: '0.236', percent: 0.236, color: '#F44336' }, // red
  { key: '0.382', percent: 0.382, color: '#FF9800' }, // yellow/orange
  { key: '0.5', percent: 0.5, color: '#4CAF50' }, // green
  { key: '0.618', percent: 0.618, color: '#009688' }, // teal (darker green)
  { key: '0.786', percent: 0.786, color: '#2196F3' }, // blue
  { key: '1', percent: 1, color: '#787B86' }, // gray
  // Extensions beyond 100%
  { key: '1.618', percent: 1.618, color: '#009688' }, // teal
  { key: '2.618', percent: 2.618, color: '#FF9800' }, // yellow/orange
  { key: '3.618', percent: 3.618, color: '#F44336' }, // red
  { key: '4.236', percent: 4.236, color: '#787B86' }, // gray
]

/** Per-instance override for one level. Every field optional — an absent field
 *  falls back to the built-in, which is what lets a future palette change still
 *  reach users who never touched that level. */
export interface FibLevelOverride {
  color?: string
  /** Absent or true = drawn. Only an explicit `false` hides the level. */
  visible?: boolean
}

/** Shape this overlay expects on `overlay.extendData`. A plain object, not a
 *  Map — it has to survive the JSON round-trip to DynamoDB. */
export interface FibExtendData {
  levels?: Record<string, FibLevelOverride>
  /** Run every level line on to the right edge of the pane, so the levels
   *  read against CURRENT price rather than stopping at the swing. Set by the
   *  auto Fib; a hand-drawn retracement leaves it unset. */
  extendRight?: boolean
  /** Skip levels above this ratio. The auto Fib stops at 1.618: the 2.618,
   *  3.618 and 4.236 extensions are noise on an engine-placed retracement. */
  maxLevel?: number
  /** Which end of the level line the label hangs off. Default: the right edge
   *  when extended, else the swing start. The auto Fib pins it to 'start' so
   *  the text sits LEFT of the line instead of over the live-price area. */
  labelSide?: 'start' | 'end'
  /** Label font size in px. Default: the overlay text default (12). */
  labelSize?: number
}

/** The read-only, engine-placed retracement: same maths and level palette as
 *  the hand-drawn tool, but locked, handle-less, never persisted, and
 *  extended to the right edge. A separate template name so the quick bar,
 *  settings drawer and remove-all logic can tell it from a user drawing. */
export const AUTO_FIB_OVERLAY_NAME = 'autoFibonacci'

interface Coordinate {
  x: number
  y: number
}

interface FibFigure {
  type: string
  key: string
  attrs: unknown
  styles?: unknown
  ignoreEvent?: boolean
}

/**
 * Pure figure builder — extracted from `createPointFigures` so the level maths
 * and the override resolution are unit-testable without a chart, mirroring
 * `riskRewardOverlay.buildFigures`.
 */
export function buildFibFigures(
  coordinates: Coordinate[],
  points: Array<{ value?: number } | undefined>,
  pricePrecision: number,
  extendData?: FibExtendData,
  /** Pane width in px; only read when `extendData.extendRight` is set. */
  paneWidth?: number,
): FibFigure[] {
  if (coordinates.length <= 1) return []
  if (!isNum(points[0]?.value) || !isNum(points[1]?.value)) return []

  const startX = Math.min(coordinates[0].x, coordinates[1].x)
  const anchorEndX = Math.max(coordinates[0].x, coordinates[1].x)
  // Extended levels run to the pane edge (the horizontal-ray technique: the
  // pane's own width, not a huge constant, so nothing off-screen leaves
  // artefacts). If the anchors already sit past the edge, keep their extent.
  const extend = extendData?.extendRight === true && isNum(paneWidth)
  const endX = extend ? Math.max(anchorEndX, paneWidth!) : anchorEndX
  // Labels hang off the swing start for a hand-drawn Fib and off the right edge
  // for an extended one, unless `labelSide` says otherwise.
  const side = extendData?.labelSide ?? (extend ? 'end' : 'start')
  const labelX = side === 'end' ? endX - 4 : startX - 4
  const maxLevel = extendData?.maxLevel
  const labelSize = extendData?.labelSize
  const yDif = coordinates[0].y - coordinates[1].y
  const valueDif = points[0]!.value! - points[1]!.value!

  const overrides = extendData?.levels
  const figures: FibFigure[] = []

  for (const level of FIB_LEVELS) {
    // Unknown keys in `overrides` are simply never looked up, so an older
    // client meeting a newer level set degrades quietly instead of throwing.
    const ov = overrides?.[level.key]
    if (ov?.visible === false) continue
    if (isNum(maxLevel) && level.percent > maxLevel) continue

    const color = ov?.color ?? level.color
    const y = coordinates[1].y + yDif * level.percent
    const value = (points[1]!.value! + valueDif * level.percent).toFixed(pricePrecision)
    const pctLabel = (level.percent * 100).toFixed(1)

    figures.push({
      type: 'line',
      key: `fib-${level.key}`,
      attrs: {
        coordinates: [
          { x: startX, y },
          { x: endX, y },
        ],
      },
      styles: { color },
    })

    figures.push({
      type: 'text',
      key: `fib-${level.key}-label`,
      ignoreEvent: true,
      attrs: {
        x: labelX,
        y,
        text: `${value} (${pctLabel}%)`,
        baseline: 'middle',
        align: 'right',
      },
      styles: { style: 'none', color, ...(isNum(labelSize) ? { size: labelSize } : {}) },
    })
  }

  return figures
}

export function registerFibonacciOverlay(): void {
  registerOverlay({
    name: 'fibonacciLine',
    totalStep: 3,
    ...DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, overlay, precision, yAxis }) =>
      buildFibFigures(
        coordinates,
        overlay.points,
        resolvePricePrecision(yAxis, precision),
        overlay.extendData as FibExtendData | undefined,
      ),
  } as OverlayTemplateParam)
}

/** Register the engine-placed auto Fib template (once). Locked and handle-less
 *  like `patternAnnotation`; reads `bounding.width` so levels reach the edge. */
export const registerAutoFibOverlay = registerOnce(() => {
  registerOverlay({
    name: AUTO_FIB_OVERLAY_NAME,
    totalStep: 3,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, overlay, precision, yAxis, bounding }) =>
      buildFibFigures(
        coordinates,
        overlay.points,
        resolvePricePrecision(yAxis, precision),
        {
          extendRight: true,
          maxLevel: 1.618,
          labelSide: 'start',
          labelSize: 10,
          ...((overlay.extendData ?? {}) as FibExtendData),
        },
        (bounding as { width?: number } | undefined)?.width,
      ).map((f) => ({ ...f, ignoreEvent: true })),
  } as OverlayTemplateParam)
})
